import { useMemo, useRef, type ReactNode } from 'react'
import { useFrame } from '@react-three/fiber'
import * as THREE from 'three'

/**
 * Peças de polia desenhadas como as de verdade: a roldana com o canal da corda
 * (perfil em U girado), as placas laterais em gota com o furo do olhal no alto
 * e o eixo atravessando tudo. As medidas são em metros; a roldana gira em
 * torno do eixo Z, e o olhal fica em +Y, a `alturaOlhalM` do eixo.
 */

const COR_ROLDANA = '#cbd5e1'
const COR_EIXO = '#64748b'
export const COR_PLACA_POLIA = '#dc2626'

/**
 * Perfil da roldana (raio × altura ao longo do eixo), girado em volta do eixo:
 * cubo no centro, flanges nas bordas e o canal em U no meio, onde a corda passa.
 */
export function geometriaDaRoldana(raioM: number, espessuraM: number): THREE.BufferGeometry {
  const meia = espessuraM / 2
  const raioCubo = raioM * 0.28
  const fundoCanal = raioM * 0.72
  const pontos = [
    new THREE.Vector2(raioCubo, -meia),
    new THREE.Vector2(raioM, -meia),
    new THREE.Vector2(raioM, -meia * 0.7),
    new THREE.Vector2(fundoCanal + (raioM - fundoCanal) * 0.35, -meia * 0.45),
    new THREE.Vector2(fundoCanal, -meia * 0.15),
    new THREE.Vector2(fundoCanal, meia * 0.15),
    new THREE.Vector2(fundoCanal + (raioM - fundoCanal) * 0.35, meia * 0.45),
    new THREE.Vector2(raioM, meia * 0.7),
    new THREE.Vector2(raioM, meia),
    new THREE.Vector2(raioCubo, meia),
  ]
  const g = new THREE.LatheGeometry(pontos, 28)
  // o torno gira em volta de Y; a roldana gira em volta de Z
  g.rotateX(Math.PI / 2)
  g.computeVertexNormals()
  return g
}

/**
 * Placa lateral em gota: um círculo grande em volta do eixo, um pequeno em
 * volta do olhal e as duas tangentes externas ligando os dois; o furo do olhal
 * atravessa a placa.
 */
export function geometriaDaPlaca(raioM: number, alturaOlhalM: number, espessuraM: number): THREE.BufferGeometry {
  const raioGrande = raioM * 1.18
  const raioOlhal = Math.max(raioM * 0.42, 0.012)
  // ângulo da tangente externa entre os dois círculos, que estão no eixo Y
  const fi = Math.asin(Math.min(0.95, (raioGrande - raioOlhal) / alturaOlhalM))
  const forma = new THREE.Shape()
  forma.absarc(0, 0, raioGrande, fi, -Math.PI - fi, true)
  forma.absarc(0, alturaOlhalM, raioOlhal, Math.PI - fi, fi, true)
  forma.closePath()
  const furo = new THREE.Path()
  furo.absarc(0, alturaOlhalM, raioOlhal * 0.55, 0, Math.PI * 2, false)
  forma.holes.push(furo)
  const g = new THREE.ExtrudeGeometry(forma, { depth: espessuraM, bevelEnabled: false, curveSegments: 18 })
  g.translate(0, 0, -espessuraM / 2)
  return g
}

export interface PropsRoldana {
  /** Raio externo da roldana, em metros. */
  readonly raioM: number
  /** Altura do olhal acima do eixo, em metros. */
  readonly alturaOlhalM: number
  /** Posições em Z de cada roldana (uma para a polia simples, duas para a dupla). */
  readonly roldanasZ?: readonly number[]
  readonly corPlaca?: string
  /** Giro de cada roldana na puxada animada, em rad/s (0 ou ausente: parada). */
  readonly giros?: readonly number[]
}

/** Gira o conteúdo em volta do eixo Z a `radPorSegundo`. Só existe quando há giro (usa o relógio da cena). */
function Girando({ radPorSegundo, children }: { radPorSegundo: number; children: ReactNode }) {
  const grupo = useRef<THREE.Group>(null)
  useFrame((_, dt) => {
    if (grupo.current) grupo.current.rotation.z += radPorSegundo * dt
  })
  return <group ref={grupo}>{children}</group>
}

/** Polia completa: roldana(s), duas placas em gota e o eixo. */
export function PoliaRealista({ raioM, alturaOlhalM, roldanasZ = [0], corPlaca = COR_PLACA_POLIA, giros = [] }: PropsRoldana) {
  const espessuraRoldana = raioM * 0.42
  const espessuraPlaca = raioM * 0.12
  const roldana = useMemo(() => geometriaDaRoldana(raioM, espessuraRoldana), [raioM, espessuraRoldana])
  const placa = useMemo(() => geometriaDaPlaca(raioM, alturaOlhalM, espessuraPlaca), [raioM, alturaOlhalM, espessuraPlaca])
  const minZ = Math.min(...roldanasZ) - espessuraRoldana / 2 - espessuraPlaca
  const maxZ = Math.max(...roldanasZ) + espessuraRoldana / 2 + espessuraPlaca
  // entre duas roldanas vizinhas há uma placa do meio, como na polia dupla de verdade
  const placasDoMeio = roldanasZ.slice(1).map((z, i) => (z + roldanasZ[i]!) / 2)
  const larguraEixo = maxZ - minZ + espessuraPlaca * 2
  return (
    <group>
      {roldanasZ.map((z, i) => {
        const malha = (
          <mesh geometry={roldana} position={[0, 0, z]} data-roldana>
            <meshStandardMaterial color={COR_ROLDANA} metalness={0.85} roughness={0.28} />
          </mesh>
        )
        const giro = giros[i] ?? 0
        return giro ? (
          <Girando key={`r${z}`} radPorSegundo={giro}>
            {malha}
          </Girando>
        ) : (
          <group key={`r${z}`}>{malha}</group>
        )
      })}
      {[minZ, maxZ, ...placasDoMeio].map((z) => (
        <mesh key={`p${z}`} geometry={placa} position={[0, 0, z]} data-placa>
          <meshStandardMaterial color={corPlaca} metalness={0.45} roughness={0.38} />
        </mesh>
      ))}
      {/* eixo, com as cabeças do parafuso para fora das placas */}
      <mesh rotation={[Math.PI / 2, 0, 0]} position={[0, 0, (minZ + maxZ) / 2]}>
        <cylinderGeometry args={[raioM * 0.16, raioM * 0.16, larguraEixo, 12]} />
        <meshStandardMaterial color={COR_EIXO} metalness={0.9} roughness={0.25} />
      </mesh>
      {[minZ - espessuraPlaca, maxZ + espessuraPlaca].map((z) => (
        <mesh key={`c${z}`} rotation={[Math.PI / 2, 0, 0]} position={[0, 0, z]}>
          <cylinderGeometry args={[raioM * 0.26, raioM * 0.26, espessuraPlaca, 6]} />
          <meshStandardMaterial color={COR_EIXO} metalness={0.9} roughness={0.25} />
        </mesh>
      ))}
    </group>
  )
}

/** Só a roldana com canal e o eixo, para os blocos do sistema de vantagem (as placas do bloco são desenhadas à parte). */
export function RoldanaComCanal({ raioM, espessuraM }: { raioM: number; espessuraM: number }) {
  const roldana = useMemo(() => geometriaDaRoldana(raioM, espessuraM), [raioM, espessuraM])
  return (
    <group>
      <mesh geometry={roldana} data-roldana>
        <meshStandardMaterial color={COR_ROLDANA} metalness={0.85} roughness={0.28} />
      </mesh>
      <mesh rotation={[Math.PI / 2, 0, 0]}>
        <cylinderGeometry args={[raioM * 0.18, raioM * 0.18, espessuraM * 1.6, 10]} />
        <meshStandardMaterial color={COR_EIXO} metalness={0.9} roughness={0.25} />
      </mesh>
    </group>
  )
}
